import {
  Avatar,
  Badge,
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { ContactPresenceDot } from "./ContactPresenceDot";
import { Contact } from "../_types/internalContacts.types";
import { ContactPersonStatus } from "../_types/workforcePresence.types";
import { isCallableContact } from "../_utils/callRoom.utils";
import {
  getContactId,
  getContactSubtitle,
  getContactTitle,
} from "../_utils/internalContacts.utils";
import { colors } from "../../ui/colors";

interface InternalContactsItemProps {
  contact: Contact;
  avatarColor: string;
  unreadCount: number;
  onSelect: (contact: Contact) => void;
  onCall?: (contact: Contact) => void;
  hideCallButton?: boolean;
  isCalling?: boolean;
  personStatus?: ContactPersonStatus | null;
}

export const InternalContactsItem = ({
  contact,
  avatarColor,
  unreadCount,
  onSelect,
  onCall,
  hideCallButton = false,
  isCalling = false,
  personStatus,
}: InternalContactsItemProps) => {
  const id = getContactId(contact);
  const title = getContactTitle(contact);
  const subtitle = getContactSubtitle(contact);
  const isCompany = contact.type === "company";
  const canCall = Boolean(onCall) && isCallableContact(contact) && !hideCallButton;

  return (
    <ListItem key={`contact-${id}`} disablePadding sx={{ px: 2 }}>
      <ListItemButton
        onClick={() => onSelect(contact)}
        sx={{
          borderRadius: "12px",
          py: 1.5,
          px: 2,
          gap: 1.5,
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ListItemAvatar>
          <Badge
            badgeContent={unreadCount}
            color="error"
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                height: "18px",
                minWidth: "18px",
                fontWeight: 700,
                border: "2px solid",
                borderColor: "background.paper",
              },
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <Avatar
                src={!isCompany ? contact.avatar || undefined : undefined}
                sx={{
                  bgcolor: avatarColor,
                  width: 40,
                  height: 40,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {(!contact.avatar || isCompany) && (title[0] || "?")}
              </Avatar>
              <ContactPresenceDot
                contactType={contact.type}
                personStatus={personStatus}
              />
            </Box>
          </Badge>
        </ListItemAvatar>

        <ListItemText
          primary={
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: "text.primary",
                lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
          }
          secondary={
            subtitle ? (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: 12,
                  lineHeight: 1.4,
                  mt: 0.25,
                }}
              >
                {subtitle}
              </Typography>
            ) : undefined
          }
        />

        {canCall && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onCall?.(contact);
            }}
            disabled={isCalling}
            aria-label="Start call"
            sx={{
              flexShrink: 0,
              color: colors.info.base,
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <PhoneIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </ListItemButton>
    </ListItem>
  );
};
